/**
 * schema.org JSON-LD builders (web plan W0.7, master D-8). Each returns plain
 * nodes without `@context`; `<PageHead/>` wraps them, and a head from
 * `./templates` adds the BreadcrumbList built from the crumbs the page shows.
 *
 * Pure module (no `window`, no React), shared by the SPA and the SEO server.
 *
 * Shapes:
 * - Home: `Organization` + `WebSite` (`buildSiteLd`), on the home page only.
 * - Brand: a `CollectionPage` about an `Organization` whose `url` is the
 *   brand's own website and whose `@id` is `{page}#org`.
 * - Promotion: a `WebPage` whose `mainEntity` is an `Offer` offered by the
 *   brand's `@id`, with its availability from the phase (never `InStock` once
 *   ended). Never `Product`, `Event` or `SaleEvent`: storewide sales are none
 *   of them, and Google's event guidelines exclude discounts.
 * - Store / shop: a `Store` with a real `PostalAddress` (never the "lat,lng"
 *   pin as an address). `geo` and `hasMap` are added only once the backend
 *   marks a location verified (B3.2); until then neither. A brand's store
 *   points at the brand's `@id` and has no `sameAs` of its own.
 * - Lists: a `CollectionPage` whose `mainEntity` is an `ItemList`.
 *
 * A page node (`WebPage`, `CollectionPage`) gets its `name` and `description`
 * from the head in `./templates` (the page's H1 and meta description) unless
 * the caller sets them, so the JSON-LD names the page exactly as the page
 * does. Only fields that exist are emitted.
 */
import type { OpeningHours, Socials } from '@/types';
import { ANDROID_APP_URL } from '@/constants/config';
import { SITE_CONTACT_EMAIL, SITE_SOCIAL_PROFILES } from '@/content/site';
import { formatSocialUrl } from '@/utils/formatSocialUrl';
import { promotionPhase, promotionWindow, type PromotionDates, type PromotionPhase } from './lifecycle';
import { countryCode, DEFAULT_LOCALE, type Locale } from './locales';
import { openingHoursSpecification } from './openingHours';
import {
  brandSeoName,
  cityLabel,
  cleanAddress,
  cleanDescription,
  cleanName,
  displayName,
  toZonedIsoString,
} from './text';
import {
  absoluteUrl,
  canonicalFor,
  catalogPath,
  entityPath,
  LIST_PAGE_SIZE,
  listPageHref,
  SITE_URL,
  type EntityRef,
} from './urls';

/** `@id` fragments: the page itself, the organization it is about, a store, an offer. */
export const LD_ID = {
  page: '#webpage',
  org: '#org',
  store: '#store',
  offer: '#offer',
} as const;

// ─── Helpers ───────────────────────────────────────────────────────────────

/** Drop undefined/empty values so JSON-LD stays clean. */
function prune<T extends Record<string, unknown>>(obj: T): T {
  for (const key of Object.keys(obj)) {
    const v = obj[key];
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
      delete obj[key];
    }
  }
  return obj;
}

/** The absolute canonical URL of a site path: the same URL as the page's canonical. */
function pageUrl(path: string): string {
  return absoluteUrl(canonicalFor(path));
}

/**
 * An absolute http(s) URL with a dotted host, or null. A bare handle, a
 * relative path or a typo is not an identity URL.
 */
function webUrl(value: string | null | undefined): string | null {
  const text = value?.trim();
  if (!text) return null;
  try {
    const url = new URL(text);
    const web = url.protocol === 'https:' || url.protocol === 'http:';
    return web && url.hostname.includes('.') ? text : null;
  } catch {
    return null;
  }
}

/** The entity's own website, as its page links it (`https://` added to a bare domain). */
function websiteUrl(website: string | null | undefined): string | null {
  return webUrl(formatSocialUrl('website', website));
}

const SOCIAL_PLATFORMS = [
  ['facebook', 'facebook'],
  ['instagram', 'instagram'],
  ['twitter', 'twitter'],
  ['tiktok', 'tiktok'],
  ['youtube', 'youtube'],
  ['linkedIn', 'linkedin'],
] as const satisfies readonly (readonly [keyof Socials, string])[];

/** Profile URLs built the way the page's social links build them, each once. */
function socialUrls(socials: Socials | null | undefined): string[] {
  const urls = SOCIAL_PLATFORMS.map(([key, platform]) =>
    webUrl(formatSocialUrl(platform, socials?.[key])),
  ).filter((url): url is string => url !== null);
  return [...new Set(urls)];
}

/** The first value with at least 7 digits: placeholders such as "00" are not numbers. */
function telephone(...values: (string | null | undefined)[]): string | undefined {
  for (const value of values) {
    const text = value?.trim();
    if (text && (text.match(/\d/g)?.length ?? 0) >= 7) return text;
  }
  return undefined;
}

/** A node described on another page: its type, `@id` and name. */
function reference(type: string, id: string, name: string): object {
  return prune({ '@type': type, '@id': id, name });
}

/** The `@id` of a brand's (or a shop's) Organization: `{its page}#org`. */
export function organizationId(entity: EntityRef, type: 'brand' | 'shop'): string {
  return `${pageUrl(entityPath(entity, type))}${LD_ID.org}`;
}

// ─── Home ──────────────────────────────────────────────────────────────────

/** The site's other names; Google's site-name feature reads them from the WebSite. */
export const SITE_ALTERNATE_NAMES: readonly string[] = ['Promo Stork', 'promostork.com'];

/** The site logo, a file under public/. */
export const SITE_LOGO_PATH = '/icon-512.png';

/**
 * OWNER CONTENT, filled in src/content/site.ts (web plan "What I need" #3):
 * PromoStork's official social profile URLs and the domain contact email.
 * Empty until the owner confirms them; nothing is emitted meanwhile. They live
 * in that tiny module because the footer and the Contact page's liveness
 * rule read them too (web plan W3.7), from the entry chunk. index.html's
 * static copy must match (src/seo/indexHtml.test.ts).
 */
export { SITE_CONTACT_EMAIL, SITE_SOCIAL_PROFILES };

/**
 * The home page's `Organization` + `WebSite` (master D-8): on `/` today, and
 * identically on every locale home once it exists (plan W2, W5.7). No
 * `SearchAction`: Google retired the sitelinks search box and /search is
 * noindex. index.html carries the same nodes statically for crawlers that do
 * not run JavaScript.
 */
export function buildSiteLd(input: {
  /** The site name (`SITE_NAME`). */
  name: string;
  /** The site description: the home description without a live offer. */
  description: string;
  locale?: Locale;
  /**
   * The nodes belong to this locale's home alone (`/bh/en#organization`,
   * `/bh/en#website`): a node's `@id` must always mean the same values, and
   * the locale homes describe the organization for their own country
   * (`areaServed`, `description`, `inLanguage`). Without it they are the
   * site's single `/#organization` and `/#website`.
   */
  scoped?: boolean;
}): object[] {
  const locale = input.locale ?? DEFAULT_LOCALE;
  const home = `${SITE_URL}/`;
  const localeHome = pageUrl(catalogPath(locale, '/'));
  const orgId = input.scoped ? `${localeHome}#organization` : `${SITE_URL}/#organization`;
  const websiteId = input.scoped ? `${localeHome}#website` : `${SITE_URL}/#website`;
  const email = SITE_CONTACT_EMAIL.trim();
  return [
    prune({
      '@type': 'Organization',
      '@id': orgId,
      name: input.name,
      alternateName: [...SITE_ALTERNATE_NAMES],
      url: home,
      logo: `${SITE_URL}${SITE_LOGO_PATH}`,
      description: input.description,
      email: email || undefined,
      areaServed: countryCode(locale),
      sameAs: [ANDROID_APP_URL, ...SITE_SOCIAL_PROFILES],
    }),
    {
      '@type': 'WebSite',
      '@id': websiteId,
      url: input.scoped ? localeHome : home,
      name: input.name,
      alternateName: [...SITE_ALTERNATE_NAMES],
      inLanguage: locale.hreflang,
      publisher: { '@id': orgId },
    },
  ];
}

// ─── Brands and shops ──────────────────────────────────────────────────────

/** A brand or shop as far as its Organization node cares. */
export interface OrganizationLdInput extends EntityRef {
  name: string;
  /** Short name used instead of `name` while staff have set one (backend SPB5). */
  seoName?: string | null;
  /** Staff "About" text (backend B3.4), the same text the page shows under "About {Name}". */
  description?: string | null;
  website?: string | null;
  phoneNumber?: string | null;
  mobileNumber?: string | null;
  socials?: Socials | null;
}

/** The "About" text as the page shows it (`cleanDescription`, paragraphs joined); undefined for none. */
function aboutText(description: string | null | undefined): string | undefined {
  return cleanDescription(description, { max: Infinity }) || undefined;
}

export interface OrganizationLdOptions {
  /** Absolute logo URL. */
  logo?: string | null;
  locale?: Locale;
}

/** The Organization itself: `url` is its own website, never our page. */
function organizationNode(entity: OrganizationLdInput, id: string, logo?: string | null): object {
  return prune({
    '@type': 'Organization',
    '@id': id,
    name: brandSeoName(entity) || displayName(entity.name),
    description: aboutText(entity.description),
    url: websiteUrl(entity.website),
    logo: logo || undefined,
    telephone: telephone(entity.phoneNumber, entity.mobileNumber),
    sameAs: socialUrls(entity.socials),
  });
}

/** A hub page (`CollectionPage`) about an Organization. */
function organizationPage(
  entity: OrganizationLdInput,
  type: 'brand' | 'shop',
  { logo, locale = DEFAULT_LOCALE }: OrganizationLdOptions,
): object {
  const url = pageUrl(entityPath(entity, type));
  return prune({
    '@type': 'CollectionPage',
    '@id': `${url}${LD_ID.page}`,
    url,
    inLanguage: locale.hreflang,
    about: organizationNode(entity, organizationId(entity, type), logo),
  });
}

/** Brand page: a `CollectionPage` about the brand's `Organization` (`@id` `{page}#org`). */
export function buildBrandLd(brand: OrganizationLdInput, options: OrganizationLdOptions = {}): object {
  return organizationPage(brand, 'brand', options);
}

// ─── Stores and shops ──────────────────────────────────────────────────────

/** A store or shop as far as its `Store` node cares. */
export interface PlaceLdInput extends OrganizationLdInput {
  address?: string | null;
  city?: string | null;
  /** Backend BG2 (`Country { code name }`); the locale's country until it ships. */
  country?: { code?: string | null } | null;
  /** Backend BG3 (`City { name slug region { name } }`): the province. Omitted until it ships. */
  cityRef?: { region?: { name?: string | null } | null } | null;
  /** Backend B3.4: the same rows the page's "Opening hours" shows. */
  openingHours?: readonly OpeningHours[] | null;
}

/**
 * A real `PostalAddress`: the typed address and city, the province once BG3
 * ships and the country (`"PK"` until BG2 ships `country.code`). Nothing when
 * there is neither an address nor a city; never the "lat,lng" map pin. The
 * street address keeps its "#" unit numbers (`cleanAddress`), exactly as the
 * page and the store's meta description show it; the city as the page shows
 * it (`cityLabel`: an all-lowercase "lahore" becomes "Lahore").
 */
function postalAddress(place: PlaceLdInput, locale: Locale): object | undefined {
  const streetAddress = cleanAddress(place.address);
  const addressLocality = cityLabel(place.city);
  if (!streetAddress && !addressLocality) return undefined;
  return prune({
    '@type': 'PostalAddress',
    streetAddress,
    addressLocality,
    addressRegion: cleanName(place.cityRef?.region?.name),
    addressCountry: place.country?.code?.trim().toUpperCase() || countryCode(locale),
  });
}

/**
 * A single place: `Store` with its address. `url` is its page here (Google
 * wants the location's own URL). No `geo` / `hasMap` until the backend marks
 * the location verified (B3.2). Its "About" text and opening hours (backend
 * B3.4) only when entered, from the rows the page shows (`./openingHours`).
 */
function storeNode(
  place: PlaceLdInput,
  url: string,
  id: string,
  { logo, locale = DEFAULT_LOCALE }: OrganizationLdOptions,
  { parent, sameAs }: { parent?: object; sameAs?: string[] } = {},
): object {
  return prune({
    '@type': 'Store',
    '@id': id,
    name: displayName(place.name),
    description: aboutText(place.description),
    url,
    image: logo || undefined,
    telephone: telephone(place.phoneNumber, place.mobileNumber),
    address: postalAddress(place, locale),
    openingHoursSpecification: openingHoursSpecification(place.openingHours),
    parentOrganization: parent,
    brand: parent,
    sameAs,
  });
}

/** The entity's website and profiles, each once: what it is the same entity as. */
function identityUrls(entity: OrganizationLdInput): string[] {
  const website = websiteUrl(entity.website);
  return [...new Set([...(website ? [website] : []), ...socialUrls(entity.socials)])];
}

export interface ShopLdOptions extends OrganizationLdOptions {
  /**
   * A shop with several branches is an `Organization` (a `CollectionPage`
   * about it, like a brand), not one `Store`. Today every shop has exactly one
   * location, so no page passes this until the backend models branches.
   */
  multiBranch?: boolean;
}

/**
 * Shop page: a `Store` (a shop is one place with its own address), whose `@id`
 * `{page}#org` is what its promotions' `offeredBy` points at. An independent
 * shop is its own business, so its website and profiles are its `sameAs`.
 */
export function buildShopLd(shop: PlaceLdInput, options: ShopLdOptions = {}): object {
  if (options.multiBranch) return organizationPage(shop, 'shop', options);
  return storeNode(shop, pageUrl(entityPath(shop, 'shop')), organizationId(shop, 'shop'), options, {
    sameAs: identityUrls(shop),
  });
}

export interface StoreLdInput extends PlaceLdInput {
  brand?: (EntityRef & { name: string; seoName?: string | null }) | null;
}

/**
 * Store page: a `Store` (`@id` `{page}#store`) whose `parentOrganization` and
 * `brand` are the brand's Organization `@id`, the node its brand page
 * describes. No `sameAs`: a store's website and profiles are its brand's
 * (every store of a brand carries the brand's site), so each branch would
 * claim to be the brand; the brand relation is the `@id` above.
 */
export function buildStoreLd(store: StoreLdInput, options: OrganizationLdOptions = {}): object {
  const url = pageUrl(entityPath(store, 'store'));
  const brand = store.brand
    ? reference('Organization', organizationId(store.brand, 'brand'), brandSeoName(store.brand) || displayName(store.brand.name))
    : undefined;
  return storeNode(store, url, `${url}${LD_ID.store}`, options, { parent: brand });
}

// ─── Promotions ────────────────────────────────────────────────────────────

/** Who runs a promotion: its brand, or else its first named shop. */
export interface PromotionMerchant {
  /** Cleaned name. */
  name: string;
  type: 'brand' | 'shop';
  ref: EntityRef;
}

/** A promotion's brand and shops, as far as its merchant is concerned. */
export interface MerchantSource {
  brand?: (EntityRef & { name: string; seoName?: string | null }) | null;
  /**
   * Participating shops. A promotion can run at shops without a brand (the
   * backend allows it); its first named shop is then the merchant.
   */
  shops?: readonly (EntityRef & { name: string })[] | null;
}

/** The brand, or else the first shop whose name survives `cleanName`; null for neither. */
export function promotionMerchant(promo: MerchantSource): PromotionMerchant | null {
  const brand = brandSeoName(promo.brand);
  if (promo.brand && brand) return { name: brand, type: 'brand', ref: promo.brand };
  for (const shop of promo.shops ?? []) {
    const name = cleanName(shop?.name);
    if (shop && name) return { name, type: 'shop', ref: shop };
  }
  return null;
}

export interface PromotionLdInput extends EntityRef, PromotionDates, MerchantSource {
  name: string;
  /** The backend's clean headline (B3.5); falls back to `cleanName(name)`. */
  headline?: string | null;
  description?: string | null;
  categories?: readonly { title?: string | null }[] | null;
}

export interface PromotionLdOptions {
  /** Absolute share image. */
  image?: string | null;
  /** The moment the phase is judged at. */
  now?: Date;
  locale?: Locale;
}

/**
 * `availability` per phase (master D-4): an announced offer is not yet
 * available (`PreOrder`, with `validFrom` giving the start), a running one is
 * `InStock`, and an ended one is `Discontinued`, never `InStock`.
 */
export const OFFER_AVAILABILITY: Readonly<Record<PromotionPhase, string>> = {
  upcoming: 'https://schema.org/PreOrder',
  active: 'https://schema.org/InStock',
  ended: 'https://schema.org/Discontinued',
};

/**
 * Promotion page: a `WebPage` whose `mainEntity` is the `Offer`, offered by
 * the brand's Organization `@id` (or, for a shop-only promotion, its first
 * shop's). `validFrom` / `validThrough` only when the dates exist, in the
 * locale's time zone (a bare `YYYY-MM-DD` is that day's 00:00 / 23:59:59, see
 * `promotionWindow`). The Offer's name and description are cleaned (no emoji,
 * hashtags or URLs).
 */
export function buildPromotionLd(promo: PromotionLdInput, options: PromotionLdOptions = {}): object {
  const locale = options.locale ?? DEFAULT_LOCALE;
  const url = pageUrl(entityPath(promo, 'promotion'));
  const merchant = promotionMerchant(promo);
  const phase = promotionPhase(promo, options.now ?? new Date(), locale);
  const { start, end } = promotionWindow(promo, locale);
  return prune({
    '@type': 'WebPage',
    '@id': `${url}${LD_ID.page}`,
    url,
    inLanguage: locale.hreflang,
    mainEntity: prune({
      '@type': 'Offer',
      '@id': `${url}${LD_ID.offer}`,
      name: cleanName(promo.headline) || cleanName(promo.name),
      description: cleanDescription(promo.description, { max: Infinity }),
      url,
      image: options.image || undefined,
      category: cleanName(promo.categories?.[0]?.title),
      validFrom: toZonedIsoString(start, locale.timeZone),
      validThrough: toZonedIsoString(end, locale.timeZone),
      availability: OFFER_AVAILABILITY[phase],
      eligibleRegion: { '@type': 'Country', name: countryCode(locale) },
      offeredBy: merchant
        ? reference('Organization', organizationId(merchant.ref, merchant.type), merchant.name)
        : undefined,
    }),
  });
}

// ─── Lists ─────────────────────────────────────────────────────────────────

/** One entry of a list page: its visible name and its page. */
export interface ListEntry {
  name: string;
  path: string;
}

export interface CollectionLdInput {
  /** The list page's own path, `?page=N` included for page N ≥ 2 (its canonical). */
  path: string;
  /** The entries of THIS page, in order. */
  items: readonly ListEntry[];
  /** Position of the first entry: `(N − 1) × page size + 1` on page N. Default 1. */
  startPosition?: number;
  /** The whole list's size across all pages, when known. */
  numberOfItems?: number | null;
  /** Default: the head's H1 and description (`./templates`). */
  name?: string | null;
  description?: string | null;
  /** With no entries, emit a `WebPage` instead of a `CollectionPage` without its list. */
  webPageWhenEmpty?: boolean;
  locale?: Locale;
}

/**
 * A list page: `CollectionPage` whose `mainEntity` is an `ItemList` of the
 * page's entries, numbered from their position in the whole list, with
 * `numberOfItems` for the whole list (schema.org: a paginated list counts all
 * its items). No ItemList while the page has no entries (`webPageWhenEmpty`: then it
 * is a plain `WebPage`, for the hubs).
 */
export function buildCollectionPageLd(input: CollectionLdInput): object {
  const locale = input.locale ?? DEFAULT_LOCALE;
  const url = pageUrl(input.path);
  const start =
    input.startPosition !== undefined && Number.isSafeInteger(input.startPosition) && input.startPosition >= 1
      ? input.startPosition
      : 1;
  const total =
    typeof input.numberOfItems === 'number' && Number.isSafeInteger(input.numberOfItems) && input.numberOfItems >= 0
      ? input.numberOfItems
      : undefined;
  const items = input.items.filter((item) => item.path);
  return prune({
    // A hub with nothing listed is a plain WebPage when asked: a CollectionPage promises its ItemList.
    '@type': items.length || !input.webPageWhenEmpty ? 'CollectionPage' : 'WebPage',
    '@id': `${url}${LD_ID.page}`,
    url,
    name: input.name?.trim(),
    description: input.description?.trim(),
    inLanguage: locale.hreflang,
    mainEntity: items.length
      ? prune({
          '@type': 'ItemList',
          numberOfItems: total,
          itemListElement: items.map((item, i) =>
            prune({
              '@type': 'ListItem',
              position: start + i,
              name: displayName(item.name),
              url: pageUrl(item.path),
            }),
          ),
        })
      : undefined,
  });
}

/**
 * Page N of a catalog list (/brands, /promotions, /shops; plan W0.11). Its
 * ItemList holds page N's own entries: the first `pageSize` items on screen,
 * because the pages infinite scroll appends below them have URLs of their own.
 */
export function buildListPageLd<T>(input: {
  /** The list's page-1 path (`/brands`). */
  basePath: string;
  /** The page the URL names (1-based). */
  page: number;
  /** The items on screen, page N's first. */
  items: readonly T[];
  entry: (item: T) => ListEntry;
  totalRecords?: number | null;
  pageSize?: number;
  name?: string | null;
  description?: string | null;
  webPageWhenEmpty?: boolean;
  locale?: Locale;
}): object {
  const page = Number.isSafeInteger(input.page) && input.page >= 1 ? input.page : 1;
  const pageSize = input.pageSize ?? LIST_PAGE_SIZE;
  return buildCollectionPageLd({
    path: listPageHref(input.basePath, page),
    items: input.items.slice(0, pageSize).map(input.entry),
    startPosition: (page - 1) * pageSize + 1,
    numberOfItems: input.totalRecords,
    name: input.name,
    description: input.description,
    webPageWhenEmpty: input.webPageWhenEmpty,
    locale: input.locale,
  });
}

// ─── Breadcrumbs ───────────────────────────────────────────────────────────

/** One breadcrumb: a visible label and a site path. */
export interface Crumb {
  name: string;
  path: string;
}

/**
 * Whether a trail is shown at all: Home plus at least one more crumb. The one
 * rule for both renderings of `head.breadcrumbs` — the BreadcrumbList
 * (`./templates`) and the visible `<Breadcrumbs>` (plan W0.8) — so a page
 * never carries one without the other (master D-8).
 */
export function hasBreadcrumbTrail(crumbs: readonly Crumb[] | null | undefined): crumbs is readonly Crumb[] {
  return Array.isArray(crumbs) && crumbs.length >= 2;
}

/**
 * BreadcrumbList from the ordered crumbs the page shows (plan W0.8 renders the
 * same array). Every crumb must point at a page that is live and indexable.
 */
export function buildBreadcrumbLd(crumbs: readonly Crumb[]): object {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: absoluteUrl(c.path),
    })),
  };
}
